import React from "react";
import { Button, Group, Box } from "@mantine/core";
import styles from "./WizardNav.module.css";

interface WizardNavProps {
  currentStep: number;
  totalSteps: number;
  onBack: () => void;
  onNext: () => void;
  onFinish: () => void;
  onSkip?: () => void;
  isLoading?: boolean;
  isNextDisabled?: boolean;
}

// Button color constants
const BUTTON_COLORS = {
  primaryTeal: "#36AFC9",
};

// Next/Finish button styles - solid teal with no hover change
const nextButtonStyles = {
  root: {
    backgroundColor: BUTTON_COLORS.primaryTeal,
    borderColor: BUTTON_COLORS.primaryTeal,
    "&:hover": {
      backgroundColor: BUTTON_COLORS.primaryTeal,
      borderColor: BUTTON_COLORS.primaryTeal,
    },
  },
};

// Back button styles - outline with primary teal
const backButtonStyles = {
  root: {
    borderColor: BUTTON_COLORS.primaryTeal,
    color: BUTTON_COLORS.primaryTeal,
  },
};

export default function WizardNav({
  currentStep,
  totalSteps,
  onBack,
  onNext,
  onFinish,
  onSkip,
  isLoading = false,
  isNextDisabled = false,
}: WizardNavProps) {
  const isFirstStep = currentStep === 1;
  const isLastStep = currentStep === totalSteps;

  return (
    <Box className={styles.navContainer}>
      <Group justify="flex-end" gap="md" className={styles.buttonGroup}>
        {onSkip && (
          <Button
            variant="subtle"
            color="teal"
            onClick={onSkip}
            disabled={isLoading}
            className={styles.skipButton}
          >
            Skip
          </Button>
        )}

        {/* Hide back button on first step instead of just disabling it */}
        {!isFirstStep && (
          <Button
            variant="outline"
            onClick={onBack}
            disabled={isLoading}
            className={styles.backButton}
            styles={backButtonStyles}
          >
            Back
          </Button>
        )}

        {isLastStep ? (
          <Button
            variant="filled"
            onClick={onFinish}
            loading={isLoading}
            disabled={isNextDisabled}
            className={styles.nextButton}
            styles={nextButtonStyles}
          >
            Finish
          </Button>
        ) : (
          <Button
            variant="filled"
            onClick={onNext}
            disabled={isLoading || isNextDisabled}
            className={styles.nextButton}
            styles={nextButtonStyles}
          >
            Next
          </Button>
        )}
      </Group>
    </Box>
  );
}
